import React, { useEffect, useState } from 'react';
import { Plus, Shield, Edit2, Trash2, X, Users, Lock, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw } from 'lucide-react';
import { useWorkspace, Permission, permissionCategories, allPermissions } from '../contexts/WorkspaceContext';
import { rolesApi, usersApi, RoleRecord, RolePermissionRow, UserRecord } from '../services/creatorApi';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
export function RolesPermissions() {
  const {
    currentUser,
    refreshRoles
  } = useWorkspace();
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleRecord | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(Object.keys(permissionCategories)));
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center" data-id="element-5792" data-mp="2b1baenwt8ijy">
        <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-5793" data-mp="2b4gxxypsjcdk" />
        <h2 className="mt-4 text-lg font-medium text-gray-900" data-id="element-5794" data-mp="263t1gxism3by">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-500" data-id="element-5795" data-mp="215hoidx7bcpi">
          Only Super Admins can manage roles and permissions.
        </p>
      </div>;
  }
  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesResult, usersResult] = await Promise.all([rolesApi.list(), usersApi.list()]);
      setRoles(rolesResult);
      setAllUsers(usersResult);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) newExpanded.delete(category);else newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };
  const togglePermission = (permission: string, isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => ({
      ...prev,
      permissions: prev.permissions.includes(permission) ? prev.permissions.filter((p: string) => p !== permission) : [...prev.permissions, permission]
    }));
  };
  const toggleAllInCategory = (perms: string[], isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => {
      const allSelected = perms.every(p => prev.permissions.includes(p));
      return {
        ...prev,
        permissions: allSelected ? prev.permissions.filter((p: string) => !perms.includes(p)) : [...new Set([...prev.permissions, ...perms])]
      };
    });
  };
  const handleCreateRole = async () => {
    if (!newRole.name.trim()) return;
    setIsSaving(true);
    try {
      const permRows: RolePermissionRow[] = newRole.permissions.map(p => ({
        Permission_Key: p
      }));
      await rolesApi.create({
        Role_Name: newRole.name,
        Description: newRole.description,
        Is_System: false,
        Role_Permissions: permRows
      });
      toast.success('Role created');
      setNewRole({
        name: '',
        description: '',
        permissions: []
      });
      setIsCreating(false);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to create role:', err);
      toast.error('Failed to create role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleStartEdit = (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role) {
      setEditForm({
        name: role.Role_Name,
        description: role.Description || '',
        permissions: (role.Role_Permissions || []).filter(Boolean).map(p => p.Permission_Key)
      });
      setEditingRole(role);
    }
  };
  const handleSaveEdit = async () => {
    if (!editingRole || !editForm.name.trim()) return;
    setIsSaving(true);
    try {
      // Preserve existing subform row IDs for permissions that already exist
      const existingPerms = (editingRole.Role_Permissions || []).filter(Boolean);
      const permRows: RolePermissionRow[] = editForm.permissions.map(p => {
        const existingRow = existingPerms.find(r => r.Permission_Key === p);
        return {
          ...(existingRow?.ID ? {
            ID: existingRow.ID
          } : {}),
          Permission_Key: p
        };
      });
      await rolesApi.update(editingRole.ID, {
        Role_Name: editForm.name,
        Description: editForm.description,
        Role_Permissions: permRows
      });
      toast.success('Role updated');
      setEditingRole(null);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error('Failed to update role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteRole = (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role?.Is_System) {
      toast.error('Cannot delete system roles');
      return;
    }
    setRoleToDelete(role || null);
  };
  const confirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await rolesApi.delete(roleToDelete.ID);
      toast.success('Role deleted');
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      toast.error('Failed to delete role');
    }
    setRoleToDelete(null);
  };
  const handleDuplicateRole = async (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (!role) return;
    setIsSaving(true);
    try {
      // Strip IDs from permission rows — this is a new record, Creator assigns new IDs
      const permRows: RolePermissionRow[] = (role.Role_Permissions || []).filter(Boolean).map(p => ({
        Permission_Key: p.Permission_Key
      }));
      await rolesApi.create({
        Role_Name: `${role.Role_Name} (Copy)`,
        Description: role.Description,
        Is_System: false,
        Role_Permissions: permRows
      });
      toast.success('Role duplicated');
      loadData();
      refreshRoles();
    } catch (err) {
      toast.error('Failed to duplicate role');
    } finally {
      setIsSaving(false);
    }
  };
  const getUsersWithRole = (roleName: string) => {
    return allUsers.filter(u => u.Role === roleName);
  };
  const formatPermissionName = (permission: string) => {
    const parts = permission.split('.');
    const action = parts[parts.length - 1];
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const renderPermissionEditor = (currentPermissions: string[], isEdit: boolean) => <div className="space-y-2" data-id="element-5796" data-mp="1utu4iau41cot">
      {Object.entries(permissionCategories).map(([category, perms]) => {
      const isExpanded = expandedCategories.has(category);
      const selectedCount = perms.filter(p => currentPermissions.includes(p)).length;
      const allSelected = selectedCount === perms.length;
      return <div key={category} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-5797" data-mp="1zlvetr7pwtc7">
            <button type="button" onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-5798" data-mp="2efqce12faw83">
              <div className="flex items-center gap-3" data-id="element-5799" data-mp="z4pf8u7449ef">
                {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-5800" data-mp="14421pdu2negh" /> : <ChevronRight className="h-4 w-4 text-gray-500" data-id="element-5801" data-mp="193endv97ifhh" />}
                <span className="font-medium text-gray-900" data-id="element-5802" data-mp="191bvt6seoean">{category}</span>
                <span className="text-xs text-gray-500" data-id="element-5803" data-mp="bhr0c0yn59im">
                  ({selectedCount}/{perms.length})
                </span>
              </div>
              <button type="button" onClick={e => {
            e.stopPropagation();
            toggleAllInCategory(perms, isEdit);
          }} className={`text-xs px-2 py-1 rounded ${allSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'} hover:opacity-80`} data-id="element-5804" data-mp="ldkdnmk6p71g">
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </button>
            {isExpanded && <div className="p-4 grid grid-cols-2 gap-2" data-id="element-5805" data-mp="26wo2ov2iomkn">
                {perms.map(perm => <label key={perm} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-id="element-5806" data-mp="21xl3vsd4rcl4">
                    <input type="checkbox" checked={currentPermissions.includes(perm)} onChange={() => togglePermission(perm, isEdit)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-5807" data-mp="1rzwzsbtmwsqa" />
                    <span className="text-sm text-gray-700" data-id="element-5808" data-mp="1e8ichssp25df">
                      {formatPermissionName(perm)}
                    </span>
                  </label>)}
              </div>}
          </div>;
    })}
    </div>;
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5809" data-mp="1p1f4ksd7visn">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5810" data-mp="1ozkj9cy19oo1" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-5811" data-mp="1k226fbgwapar">
      <div className="flex items-center justify-between" data-id="element-5812" data-mp="1xtpee8k79m9d">
        <div data-id="element-5813" data-mp="1xt7cavauwdtj">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5814" data-mp="1xvthioa9yh8l">
            Roles & Permissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5815" data-mp="1tuhp6b00nhwp">
            Manage user roles and their access permissions.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5816" data-mp="11481p9msuzy6">
          <button onClick={loadData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5817" data-mp="115j6ncrzedqv">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5818" data-mp="1a6h7xnb1c75r" />
          </button>
          <button onClick={() => setIsCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-5819" data-mp="2qk7hvaou5pnb">
            <Plus className="h-4 w-4" data-id="element-5820" data-mp="2y1njflazsy2s" />
            Create Role
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-5821" data-mp="2k35z7xkaq3pm">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5822" data-mp="2k4h1fsy3f3em">
          <div className="flex items-center gap-3" data-id="element-5823" data-mp="2p1qxhrfzmdbf">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-5824" data-mp="3cq9vne5bp9ps">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-5825" data-mp="3cswewb1ktdnu" />
            </div>
            <div data-id="element-5826" data-mp="3hol8lxqx00c8">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5827" data-mp="3hp3dtjvk1owa">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5828" data-mp="32z5rl9h6r99q">Total Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5829" data-mp="3vpnvgcpqheo7">
          <div className="flex items-center gap-3" data-id="element-5830" data-mp="3vnktcubb3v34">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-5831" data-mp="4b9n23lostsg">
              <Lock className="h-5 w-5 text-blue-600" data-id="element-5832" data-mp="4ct807iaft0k" />
            </div>
            <div data-id="element-5833" data-mp="e6gopq97pmi3">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5834" data-mp="e4w00h9kwxs5">
                {roles.filter(r => r.Is_System).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5835" data-mp="4816kcs5xnkq">System Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5836" data-mp="65vigkfjvtr">
          <div className="flex items-center gap-3" data-id="element-5837" data-mp="3re0higm1jvt7">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-5838" data-mp="3reth8xml1l1u">
              <Users className="h-5 w-5 text-green-600" data-id="element-5839" data-mp="3mmiezo4ddn64" />
            </div>
            <div data-id="element-5840" data-mp="3hnp3u2ck2894">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5841" data-mp="3hn5qz01ria1y">
                {allPermissions.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5842" data-mp="3cpx8eycpc406">Available Permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      {isCreating && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5843" data-mp="33oz4sh66dyi4">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5844" data-mp="1j5vkq48ekd67">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsCreating(false)} data-id="element-5845" data-mp="1j7zsix9f9wbr" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-5846" data-mp="1s6blh5xrh67d">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-5847" data-mp="1x4d8u327zron">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5848" data-mp="10gp3ymr65vn1">
                  Create New Role
                </h3>
                <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5849" data-mp="14ewhl8qc6gu0">
                  <X className="h-5 w-5" data-id="element-5850" data-mp="1e7u3loujenxd" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-5851" data-mp="gs6hwe1gmyck">
                <div data-id="element-5852" data-mp="lq8252amh3pm">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5853" data-mp="lppyhklmrn7o">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.name} onChange={e => setNewRole({
                ...newRole,
                name: e.target.value
              })} placeholder="e.g., Content Manager" data-id="element-5854" data-mp="2oderu09nwdgk" />
                </div>
                <div data-id="element-5855" data-mp="3g42oaz0e4unw">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5856" data-mp="3g4m8ynhfdx5m">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.description} onChange={e => setNewRole({
                ...newRole,
                description: e.target.value
              })} placeholder="Brief description..." data-id="element-5857" data-mp="3c3iplr065izn" />
                </div>
                <div data-id="element-5858" data-mp="12tjdtzs8lyg2">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5859" data-mp="12t1a6i38why4">
                    Permissions
                  </label>
                  {renderPermissionEditor(newRole.permissions, false)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-5860" data-mp="stiu4cqghgbc">
                <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5861" data-mp="eygozrtfnuzl">
                  Cancel
                </button>
                <button onClick={handleCreateRole} disabled={!newRole.name.trim() || isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5862" data-mp="2e8ggichcgm5w">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5863" data-mp="22vpt8hlxgt51" />}
                  Create Role
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Role Modal */}
      {editingRole && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5864" data-mp="2qkila2zfigud">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5865" data-mp="2lsqzs6v4r1b3">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditingRole(null)} data-id="element-5866" data-mp="2lqo5vbquwo13" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-5867" data-mp="3e9bh7zg1wooz">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-5868" data-mp="39hjrts8msp0f">
                <div data-id="element-5869" data-mp="3gurll2yzpzfc">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5870" data-mp="3gvkmvof6jwq3">
                    Edit Role
                  </h3>
                  {editingRole?.Is_System && <p className="text-xs text-purple-600 flex items-center gap-1 mt-1" data-id="element-5871" data-mp="3qqupad9hq64e">
                      <Lock className="h-3 w-3" data-id="element-5872" data-mp="3vrs3yfvaki15" /> System role — name cannot be
                      changed
                    </p>}
                </div>
                <button onClick={() => setEditingRole(null)} className="text-gray-400 hover:text-gray-500" data-id="element-5873" data-mp="8lrh2jo89dzr">
                  <X className="h-5 w-5" data-id="element-5874" data-mp="deuc6w8nkvi2" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-5875" data-mp="ibb1htrl9fwq">
                <div data-id="element-5876" data-mp="nc8g5wde3rth">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5877" data-mp="ncrxpbz4oe6z">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" value={editForm.name} onChange={e => setEditForm({
                ...editForm,
                name: e.target.value
              })} disabled={editingRole?.Is_System} data-id="element-5878" data-mp="1cn7elgisyzhu" />
                </div>
                <div data-id="element-5879" data-mp="ow1xky14m15y">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5880" data-mp="13mj06kphyx4f">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={editForm.description} onChange={e => setEditForm({
                ...editForm,
                description: e.target.value
              })} data-id="element-5881" data-mp="yvaqdnk999qq" />
                </div>
                <div data-id="element-5882" data-mp="28vdfcsr8irbp">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5883" data-mp="28vwww8cz3dp7">
                    Permissions
                  </label>
                  {renderPermissionEditor(editForm.permissions, true)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-5884" data-mp="35kusttwglhny">
                <button onClick={() => setEditingRole(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5885" data-mp="2rktq08wmw05u">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5886" data-mp="1l4hrg6pwgg9q">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5887" data-mp="1lfpn8v9o2ql6" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5888" data-mp="1cnnlmtnv349b">
        {roles.map(role => {
        const roleUsers = getUsersWithRole(role.Role_Name);
        const isSelected = selectedRole === role.ID;
        const permCount = role.Role_Permissions?.length || 0;
        return <div key={role.ID} className={`bg-white border rounded-lg overflow-hidden transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5889" data-mp="17nrggr3fh6s6">
              <div className="p-4 cursor-pointer" onClick={() => setSelectedRole(isSelected ? null : role.ID)} data-id="element-5890" data-mp="xpbratnyo7lb">
                <div className="flex items-start justify-between mb-3" data-id="element-5891" data-mp="a3hxxyt3ep93">
                  <div className="flex items-center gap-2" data-id="element-5892" data-mp="o2hhwt5nufyx">
                    <div className={`p-2 rounded-lg ${role.Is_System ? 'bg-purple-50' : 'bg-blue-50'}`} data-id="element-5893" data-mp="o3sof0qhq1tq">
                      <Shield className={`h-5 w-5 ${role.Is_System ? 'text-purple-600' : 'text-blue-600'}`} data-id="element-5894" data-mp="y05gju6e07md" />
                    </div>
                    <div data-id="element-5895" data-mp="16sza8wmkz9jr">
                      <h3 className="font-medium text-gray-900" data-id="element-5896" data-mp="16t8q3rdww0bq">
                        {role.Role_Name}
                      </h3>
                      {role.Is_System && <span className="inline-flex items-center gap-1 text-xs text-purple-600" data-id="element-5897" data-mp="1gr6fwjbf656g">
                          <Lock className="h-3 w-3" data-id="element-5898" data-mp="1bs3gbee7kr5v" /> System
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-5899" data-mp="9gifslzn3x8t">
                    <button onClick={e => {
                  e.stopPropagation();
                  handleDuplicateRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Duplicate" data-id="element-5900" data-mp="dfhhzytzlfwk">
                      <Copy className="h-4 w-4" data-id="element-5901" data-mp="s79n7i02hdkk" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  handleStartEdit(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-5902" data-mp="x2ovzvj7wja4">
                      <Edit2 className="h-4 w-4" data-id="element-5903" data-mp="37w1hjbsaft8l" />
                    </button>
                    {!role.Is_System && <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-5904" data-mp="30d1xr50wzzop">
                        <Trash2 className="h-4 w-4" data-id="element-5905" data-mp="2lm1gcai6wvgf" />
                      </button>}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2" data-id="element-5906" data-mp="3edl5eb1gx3qc">
                  {role.Description}
                </p>
                <div className="flex items-center justify-between text-sm" data-id="element-5907" data-mp="39bnfwz2h5coy">
                  <span className="text-gray-500" data-id="element-5908" data-mp="35b3irvhwi92t">
                    <span className="font-medium text-gray-900" data-id="element-5909" data-mp="1xybib5xb5qb8">
                      {permCount}
                    </span>{' '}
                    permissions
                  </span>
                  <span className="text-gray-500" data-id="element-5910" data-mp="1tz2mzwmmdkdk">
                    <span className="font-medium text-gray-900" data-id="element-5911" data-mp="3hscv2x6wh8ya">
                      {roleUsers.length}
                    </span>{' '}
                    users
                  </span>
                </div>
              </div>

              {isSelected && <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3" data-id="element-5912" data-mp="3cu1oqwftdgkt">
                  <div data-id="element-5913" data-mp="3crym9cxh5uz7">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5914" data-mp="4dzuyolg6xs1">
                      Permissions by Category
                    </h4>
                    <div className="space-y-1" data-id="element-5915" data-mp="9bi52ib7bj1m">
                      {Object.entries(permissionCategories).map(([category, perms]) => {
                  const rolePerms = (role.Role_Permissions || []).filter(Boolean).map(p => p.Permission_Key);
                  const count = perms.filter(p => rolePerms.includes(p)).length;
                  if (count === 0) return null;
                  return <div key={category} className="flex items-center justify-between text-sm" data-id="element-5916" data-mp="27mi92c0ho7yu">
                              <span className="text-gray-600" data-id="element-5917" data-mp="2hmiuq4ltz067">{category}</span>
                              <span className={`font-medium ${count === perms.length ? 'text-green-600' : 'text-blue-600'}`} data-id="element-5918" data-mp="1tutwk7mbvhhc">
                                {count}/{perms.length}
                              </span>
                            </div>;
                })}
                    </div>
                  </div>
                  {roleUsers.length > 0 && <div data-id="element-5919" data-mp="166ki5qrg1w08">
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5920" data-mp="1659fju9qiuap">
                        Users with this role
                      </h4>
                      <div className="flex flex-wrap gap-1" data-id="element-5921" data-mp="1zvjmrkyrgx8h">
                        {roleUsers.slice(0, 5).map(user => <span key={user.ID} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-200 text-gray-700" data-id="element-5922" data-mp="24m8ft4q3a29q">
                            {user.User_Name}
                          </span>)}
                        {roleUsers.length > 5 && <span className="text-xs text-gray-500" data-id="element-5923" data-mp="1lvqx7syz0n23">
                            +{roleUsers.length - 5} more
                          </span>}
                      </div>
                    </div>}
                </div>}
            </div>;
      })}
      </div>

      {roles.length === 0 && <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5924" data-mp="qmx2ja6u9f0l">
          <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-5925" data-mp="qorew041nyt2" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5926" data-mp="loc4nhf277fb">
            No roles defined
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5927" data-mp="gvanf2rdqvsk">
            Create roles in Creator or click "Create Role" above.
          </p>
        </div>}

      <ConfirmModal isOpen={!!roleToDelete} title="Delete Role" message={`Delete role "${roleToDelete?.Role_Name}"?`} confirmLabel="Delete" variant="danger" onCancel={() => setRoleToDelete(null)} onConfirm={confirmDelete} data-id="element-5928" data-mp="7wyp0ek9utpk" />
    </div>;
}